import { gql, type QueryHookOptions, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import type { Query, ShoreStatus } from '@/types/GraphqlSchema';
import type { ValuationsFilters as ValuationsFiltersType } from '../../store/valuationsStore';
import { AutoCompleteSimpleComposable } from './AutoCompleteSimpleComposable';

const SHORE_STATUS_QUERY = gql`
  {
    shoreStatus {
      id
      name
      assetsCount
    }
  }
`;

type ShoreStatusesProps = {
  filters: ValuationsFiltersType;
  onChange: (shoreStatuses: ShoreStatus[]) => void;
  context: string;
};

type ShoreStatusesQueryData = Pick<Query, 'shoreStatus'>;

const name = 'Shore Status';

export const ShoreStatusesFilter = ({ filters, onChange }: ShoreStatusesProps) => {
  const queryOptions: QueryHookOptions<ShoreStatusesQueryData> = {
    notifyOnNetworkStatusChange: true,
  };

  const { data, loading } = useQuery<ShoreStatusesQueryData>(SHORE_STATUS_QUERY, queryOptions);

  const shoreStatuses = useMemo(() => {
    const statuses = data?.shoreStatus;
    if (!Array.isArray(statuses)) {
      return [];
    }
    return statuses.filter(item => item.name.toLowerCase() !== 'pre-salt' && item.assetsCount > 0);
  }, [data?.shoreStatus]);

  const shoreStatusesValue = useMemo(() => {
    const filterShoreStatuses = filters.shoreStatuses;
    if (Array.isArray(filterShoreStatuses)) {
      return filterShoreStatuses;
    }
    if (filterShoreStatuses && typeof filterShoreStatuses === 'object') {
      return [filterShoreStatuses];
    }
    return [];
  }, [filters.shoreStatuses]);

  return (
    <AutoCompleteSimpleComposable<ShoreStatus>
      options={shoreStatuses}
      loading={loading}
      value={shoreStatusesValue}
      multiple={true}
      size='medium'
      getOptionLabel={option => (typeof option === 'string' ? option : option.name || '')}
      onChange={(_, newValue) => {
        onChange((newValue as ShoreStatus[]) || []);
      }}
      renderInput={params => (
        <AutoCompleteSimpleComposable.StyledInput
          params={params}
          label={name}
          data-id='shore-statuses-filter-input'
        />
      )}
      renderTags={(value, getTagProps) => {
        const numTags = value.length;
        return (
          <>
            {value.slice(0, 1).map((option, index) => (
              <AutoCompleteSimpleComposable.StyledTag
                key={option.id || index}
                option={option}
                index={index}
                getTagProps={getTagProps}
                getOptionLabel={opt => opt.name || ''}
              />
            ))}
            {numTags > 1 && ` +${numTags - 1}`}
          </>
        );
      }}
      renderOption={(props, option, state) => {
        return (
          <AutoCompleteSimpleComposable.Option
            {...props}
            option={option}
            state={state}
            getOptionLabel={opt => opt.name || ''}
          />
        );
      }}
      data-id='shore-statuses-filter'
    />
  );
};
